import dynamic from 'next/dynamic'
import BlockContent from '@sanity/block-content-to-react'
import cx from 'classnames'

import {
  SanityHero,
  SanityHeroBackgroundType,
  SanityHeroContentPosition,
} from '@data/sanity/queries/types/modules'
import { SanityVideoAspectRatioValue } from '@data/sanity/queries/types/video'
import { serializers } from '@lib/serializers'
import { getVimeoVideoId } from '@lib/video'

import Photo from '@components/photo'

const MuxVideo = dynamic(() => import('@components/video/mux-video'))
const VimeoVideoLoop = dynamic(
  () => import('@components/video/vimeo-video-loop')
)

type HeroProps = SanityHero & {
  overlay?: boolean
}

const Hero = ({
  content,
  contentPosition,
  bgType,
  photos,
  overlay,
  vimeoVideo,
  muxVideo,
}: HeroProps) => {
  const vimeoVideoId = getVimeoVideoId(vimeoVideo?.link)

  const center = contentPosition === SanityHeroContentPosition.CENTER
  const bottomLeft = contentPosition === SanityHeroContentPosition.BOTTOM_LEFT

  return (
    <section
      className={cx(
        'relative overflow-hidden bg-pageBG max-h-screen min-h-[500px] xs:min-h-[auto]',
        {
          'before:absolute before:inset-0 before:z-[1] before:bg-black before:bg-opacity-60':
            overlay,
        }
      )}
    >
      <div
        className={cx('container absolute inset-0 z-10 flex flex-col', {
          'justify-end': bottomLeft,
          'justify-center items-center': center,
        })}
      >
        {content && (
          <div
            className={cx('z-10 w-full text-white', {
              'md:max-w-[70%] xl:max-w-[50%] mb-4 md:mb-10': bottomLeft,
              'lg:max-w-[55%]': center,
            })}
          >
            <BlockContent
              renderContainerOnSingleChild
              className={cx('rc rc-hero', { 'text-center': center })}
              blocks={content}
              serializers={serializers}
            />
          </div>
        )}
      </div>

      <div>
        {bgType === SanityHeroBackgroundType.VIMEO_VIDEO &&
          vimeoVideo &&
          vimeoVideoId && (
            <VimeoVideoLoop title={vimeoVideo.name} id={vimeoVideoId} />
          )}

        {bgType === SanityHeroBackgroundType.MUX_VIDEO && muxVideo && (
          <MuxVideo
            video={muxVideo}
            showControls={false}
            autoplay={true}
            muted={true}
            loop={true}
            customAspectRatio={{
              desktop: SanityVideoAspectRatioValue.ASPECT_16_9,
              mobile: SanityVideoAspectRatioValue.ASPECT_5_7,
            }}
          />
        )}

        {bgType === SanityHeroBackgroundType.PHOTO && photos && (
          <>
            {photos.desktopPhoto && (
              <Photo
                image={photos.desktopPhoto}
                layout="fill"
                objectFit="cover"
                priority={true}
                className="relative w-full z-0 h-full hidden sm:block"
                style={{
                  paddingTop: `${
                    100 / (photos.desktopPhoto.customRatio ?? 16 / 9)
                  }%`,
                }}
              />
            )}
            {photos.mobilePhoto && (
              <Photo
                image={photos.mobilePhoto}
                layout="fill"
                objectFit="cover"
                priority={true}
                className="relative w-full z-0 h-full sm:hidden"
                style={{
                  paddingTop: `${
                    100 / (photos.mobilePhoto.customRatio ?? 5 / 7)
                  }%`,
                }}
              />
            )}
          </>
        )}
      </div>
    </section>
  )
}

export default Hero
